import {
    Box,
    Chip,
    Grid,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import VIPModal from './VIPModal';
import { useGetModuleAccessQuery } from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import { camelCaseToTitleCase } from '../../../../shared/utils';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

type VipItem = {
    id: number;
    module?: { id?: number; name?: string };
    users?: Array<{ id?: number; fullName?: string; email?: string }>;
    roles?: string[];
};

type RoleKey = 'company_admin' | 'team_admin' | 'user' | 'view_only_user';

const ROLE_LABELS: Record<RoleKey, string> = {
    company_admin: 'Company Admin',
    team_admin: 'Team Admin',
    user: 'User',
    view_only_user: 'View Only User',
};

const UserAccessControlVIP = () => {
    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const [openModal, setOpenModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentIndex, setCurrentIndex] = useState<number | null>(null);
    const [selectedRow, SetSelectedRow] = useState<number | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const { data: getModuleAccess = [], isFetching } = useGetModuleAccessQuery(
        `/guests/${loggedInUser?.company}/module-access`,
    );

    const ordered = React.useMemo(
        () =>
            (getModuleAccess ?? [])
                .slice()
                .sort((a: VipItem, b: VipItem) => a.id - b.id),
        [getModuleAccess],
    );
    const handleOpenModal = () => {
        setIsEditing(false);
        setCurrentIndex(null);
        SetSelectedRow(null);

        const next = new URLSearchParams(searchParams);
        next.delete('selectedRow');
        setSearchParams(next, { replace: true });
        setOpenModal(true);
    };

    const handleRowClick = (row: VipItem) => {
        setIsEditing(true);
        const idx = ordered.findIndex((j: VipItem) => j.id === row?.id);
        setCurrentIndex(idx >= 0 ? idx : null);
        SetSelectedRow(row?.id ?? null);

        const next = new URLSearchParams(searchParams);
        if (row?.id) next.set('selectedRow', String(row.id));
        setSearchParams(next, { replace: true });

        setOpenModal(true);
    };

    const handleNavigate = (dir: 'prev' | 'next') => {
        setCurrentIndex((prev) => {
            const len = ordered.length;
            if (prev == null || len === 0) return prev ?? 0;

            const nextIdx =
                dir === 'prev'
                    ? Math.max(0, prev - 1)
                    : Math.min(len - 1, prev + 1);
            const nextRow = ordered[nextIdx];

            SetSelectedRow(nextRow?.id ?? null);

            const url = new URLSearchParams(searchParams);
            if (nextRow?.id) url.set('selectedRow', String(nextRow.id));
            else url.delete('selectedRow');
            setSearchParams(url, { replace: true });

            return nextIdx;
        });
    };

    const roleLabel = (r?: string) => {
        if (!r) return '';
        return ROLE_LABELS[r as RoleKey] ?? r;
    };
    const userLabel = (u?: {
        fullName?: string;
        email?: string;
        id?: number;
    }) =>
        u?.fullName
            ? camelCaseToTitleCase(u.fullName)
            : (u?.email ?? String(u?.id ?? ''));

    return (
        <Grid
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Grid sx={{ display: 'flex', flexDirection: 'row' }}>
                <Grid sx={{ width: '100%' }}>
                    <Typography variant='h1'>VIP Access Control</Typography>
                    <Typography
                        sx={{
                            fontSize: '12px',
                            pt: '10px',
                        }}
                    >
                        Give your top employees the exclusive treatment they
                        deserve. With VIP Access Control, you can mark specific
                        experiences as VIP-only, ensuring that only employees on
                        your curated VIP list are able to book them. Perfect for
                        offering premium perks, rewarding loyalty, or keeping
                        certain experiences private.
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '12px',
                            pt: '10px',
                        }}
                    >
                        Note: If you exclude a group that you are apart of you
                        will no longer see that listing on the 'Experiences'
                        page.
                    </Typography>
                </Grid>

                <Grid sx={{ textAlign: ' right', width: '10%' }}>
                    {isMobile || isIpad ? (
                        <CustomIconButton
                            variant='contained'
                            color='primary'
                            onClick={handleOpenModal}
                        >
                            <AddCircleIcon />
                        </CustomIconButton>
                    ) : (
                        <CustomButton
                            id='addFeeType'
                            variant='contained'
                            color='primary'
                            startIcon={<AddCircleIcon />}
                            onClick={handleOpenModal}
                        >
                            Add New
                        </CustomButton>
                    )}
                </Grid>
            </Grid>

            <Box
                sx={{
                    border: '1px solid',
                    borderColor: 'grey.400',
                    borderRadius: 1,
                    overflow: 'hidden',
                    marginTop: '30px',
                }}
            >
                {!getModuleAccess.length ? (
                    <Typography
                        sx={{ fontSize: 12, py: '20px', textAlign: 'center' }}
                    >
                        No VIP Access has been created
                    </Typography>
                ) : (
                    <TableContainer
                        sx={{
                            borderTop: '1px solid',
                            borderColor: 'grey.300',
                            height: 'auto',
                            overflowY: 'auto',
                            position: 'relative',
                        }}
                    >
                        <Table
                            stickyHeader
                            size='small'
                            sx={{
                                [`& .${tableCellClasses.root}`]: {
                                    borderBottom: '1px solid',
                                    borderColor: 'grey.300',
                                },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Experience</TableCell>
                                    <TableCell>VIP Roles Allowed</TableCell>
                                    <TableCell>Users Allowed</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {ordered.map((vipAccess: VipItem) => {
                                    const roles = vipAccess.roles ?? [];
                                    const users = vipAccess.users ?? [];
                                    const roleRemainders = isMobile
                                        ? roles.length
                                        : Math.max(0, roles.length - 2);
                                    const userRemainders = isMobile
                                        ? users.length
                                        : Math.max(0, users.length - 2);

                                    return (
                                        <TableRow
                                            key={vipAccess.id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                handleRowClick(vipAccess)
                                            }
                                        >
                                            <TableCell>
                                                #{vipAccess.id}
                                            </TableCell>
                                            <TableCell>
                                                {vipAccess.module?.name ?? '-'}
                                            </TableCell>

                                            <TableCell>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    {isMobile ? (
                                                        <>
                                                            {roles.length >
                                                            0 ? (
                                                                <Chip
                                                                    size='small'
                                                                    sx={{
                                                                        height: 'auto',
                                                                        fontSize:
                                                                            '10px',
                                                                    }}
                                                                    label={`+ ${roles.length}`}
                                                                />
                                                            ) : (
                                                                <Chip
                                                                    size='small'
                                                                    label='None'
                                                                />
                                                            )}
                                                        </>
                                                    ) : roles.length ? (
                                                        <>
                                                            {roles
                                                                .slice(0, 2)
                                                                .map(
                                                                    (
                                                                        r,
                                                                        idx,
                                                                    ) => (
                                                                        <CustomChip
                                                                            key={
                                                                                r ??
                                                                                idx
                                                                            }
                                                                            variant='filled'
                                                                            sx={{
                                                                                mr: 0.5,
                                                                                mb: 0.5,
                                                                                backgroundColor:
                                                                                    'lightgrey',
                                                                            }}
                                                                            label={roleLabel(
                                                                                r,
                                                                            )}
                                                                        />
                                                                    ),
                                                                )}
                                                            {roles.length >
                                                                2 && (
                                                                <Chip
                                                                    size='small'
                                                                    sx={{
                                                                        height: 'auto',
                                                                        fontSize:
                                                                            '10px',
                                                                    }}
                                                                    label={`+ ${
                                                                        roles.length -
                                                                        2
                                                                    }`}
                                                                />
                                                            )}
                                                        </>
                                                    ) : (
                                                        <Chip
                                                            size='small'
                                                            label='None'
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>

                                            <TableCell>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    {isMobile ? (
                                                        <>
                                                            {users.length >
                                                            0 ? (
                                                                <Chip
                                                                    size='small'
                                                                    sx={{
                                                                        height: 'auto',
                                                                        fontSize:
                                                                            '10px',
                                                                    }}
                                                                    label={`+ ${users.length}`}
                                                                />
                                                            ) : (
                                                                <Chip
                                                                    size='small'
                                                                    label='None'
                                                                />
                                                            )}
                                                        </>
                                                    ) : users.length ? (
                                                        <>
                                                            {users
                                                                .slice(0, 2)
                                                                .map(
                                                                    (
                                                                        u,
                                                                        idx,
                                                                    ) => (
                                                                        <CustomChip
                                                                            key={
                                                                                u?.id ??
                                                                                u?.email ??
                                                                                idx
                                                                            }
                                                                            variant='filled'
                                                                            sx={{
                                                                                mr: 0.5,
                                                                                mb: 0.5,
                                                                                backgroundColor:
                                                                                    'lightgrey',
                                                                            }}
                                                                            label={userLabel(
                                                                                u,
                                                                            )}
                                                                        />
                                                                    ),
                                                                )}
                                                            {users.length >
                                                                2 && (
                                                                <Chip
                                                                    size='small'
                                                                    sx={{
                                                                        height: 'auto',
                                                                        fontSize:
                                                                            '10px',
                                                                    }}
                                                                    label={`+ ${
                                                                        users.length -
                                                                        2
                                                                    }`}
                                                                />
                                                            )}
                                                        </>
                                                    ) : (
                                                        <Chip
                                                            size='small'
                                                            label='None'
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <CustomIconButton
                                                    onClick={(e: {
                                                        stopPropagation: () => void;
                                                    }) => {
                                                        e.stopPropagation();
                                                        handleRowClick(
                                                            vipAccess,
                                                        );
                                                    }}
                                                    sx={{
                                                        '&:hover': {
                                                            cursor: 'pointer',
                                                            color: theme.palette
                                                                .textAccent
                                                                .main,
                                                            backgroundColor:
                                                                'transparent',
                                                            background: 'none',
                                                        },
                                                    }}
                                                >
                                                    <ArrowOutwardIcon
                                                        sx={{
                                                            fontSize: '14px',
                                                        }}
                                                    />
                                                </CustomIconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

            <VIPModal
                id={selectedRow}
                moduleItems={ordered}
                index={currentIndex}
                open={openModal}
                isEditing={isEditing}
                onClose={() => setOpenModal(false)}
                onNavigate={handleNavigate}
            />
        </Grid>
    );
};

export default UserAccessControlVIP;
